import { PlanType } from '@showbie-socrative/socrative-utils';
import { isAxiosError } from '~api/isAxiosError';
import { z } from 'zod/v4';

export enum QuotaBucket {
  Question = 'QuestionGeneration',
  Insights = 'Insights',
  Import = 'Import',
  QuestionAssist = 'QuestionAssist',
}

export const PlanTypeSchema = z.enum(PlanType);
export const QuotaBucketSchema = z.enum(QuotaBucket);

export const QuotaExceededErrorSchema = z.object({
  error: z.literal('quota_exceeded'),
  bucket: QuotaBucketSchema,
  reset_at: z.iso.datetime(),
  current_plan: PlanTypeSchema.optional(),
  upgrade_to: PlanTypeSchema.optional(),
});

export type QuotaExceededError = z.infer<typeof QuotaExceededErrorSchema>;

export function parseQuotaExceededError(
  error: unknown
): QuotaExceededError | null {
  const data = isAxiosError(error) ? error.response?.data : error;
  const result = QuotaExceededErrorSchema.safeParse(data);
  return result.success ? result.data : null;
}

export const RateLimitExceededErrorSchema = z.object({
  error: z.literal('rate_limit_exceeded'),
  bucket: QuotaBucketSchema,
  retry_after: z.number().nonnegative(),
});

export type RateLimitExceededError = z.infer<
  typeof RateLimitExceededErrorSchema
>;

export function parseRateLimitExceededError(
  error: unknown
): RateLimitExceededError | null {
  const data = isAxiosError(error) ? error.response?.data : error;
  const result = RateLimitExceededErrorSchema.safeParse(data);
  return result.success ? result.data : null;
}

export const GlobalRateLimitExceededErrorSchema = z.object({
  message: z.string(),
  code: z.literal('global_rate_limit_exceeded'),
});

export type GlobalRateLimitExceededError = z.infer<
  typeof GlobalRateLimitExceededErrorSchema
>;

export function parseGlobalRateLimitExceededError(
  error: unknown
): GlobalRateLimitExceededError | null {
  const data = isAxiosError(error) ? error.response?.data : error;
  const result = GlobalRateLimitExceededErrorSchema.safeParse(data);
  return result.success ? result.data : null;
}

export const ServiceUnavailableErrorSchema = z.object({
  error: z.literal('service_unavailable'),
});

export type ServiceUnavailableError = z.infer<
  typeof ServiceUnavailableErrorSchema
>;

export function parseServiceUnavailableError(
  error: unknown
): ServiceUnavailableError | null {
  const data = isAxiosError(error) ? error.response?.data : error;
  const result = ServiceUnavailableErrorSchema.safeParse(data);
  return result.success ? result.data : null;
}

export type Ai429Error =
  | ({ kind: 'quota_exceeded' } & QuotaExceededError)
  | ({ kind: 'rate_limit_exceeded' } & RateLimitExceededError)
  | ({ kind: 'global_rate_limit_exceeded' } & GlobalRateLimitExceededError);

export function parseAi429Error(error: unknown): Ai429Error | null {
  const quota = parseQuotaExceededError(error);
  if (quota) return { kind: 'quota_exceeded', ...quota };

  const rateLimit = parseRateLimitExceededError(error);
  if (rateLimit) return { kind: 'rate_limit_exceeded', ...rateLimit };

  const globalRateLimit = parseGlobalRateLimitExceededError(error);
  if (globalRateLimit)
    return { kind: 'global_rate_limit_exceeded', ...globalRateLimit };

  return null;
}
